import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    // Initial Setup
    this.#init();

    // Use ResizeObserver instead of window.resize
    // This fixes the "Image Loading" stutter AND the "Mobile Scroll" jump
    this.resizeObserver = new ResizeObserver(debounce((entries) => {
        this.#handleResize();
    }, 200));
    
    this.resizeObserver.observe(this.refs.wrapper);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  #init() {
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed() {
    // Recalculate speed based on current width
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    
    // Safety check for empty width
    if (marqueeWidth === 0) return;

    const { marqueeItems } = this.refs;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;
    
    // Ensure we don't divide by zero
    const safeItemWidth = marqueeRepeatedItemWidth === 0 ? 1 : marqueeRepeatedItemWidth;
    
    const count = Math.ceil(marqueeWidth / safeItemWidth);
    const speed = Math.sqrt(count) * speedFactor;

    this.style.setProperty('--marquee-speed', `${speed}s`);
  }

  #handleResize() {
    const { marqueeItems } = this.refs;
    // Recalculate copies needed based on new width
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = marqueeItems.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
      this.#duplicateContent(); // Re-clone if we added items
    } else if (newNumberOfCopies < currentNumberOfCopies) {
        // Optional: reduce items, but usually safer to leave them to prevent jumps
    }

    this.#setSpeed();
    // We do NOT restart animation here to prevent visual jumping
  }

  #duplicateContent() {
    // Remove old clone if it exists to prevent duplicates
    this.clonedContent?.remove();

    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;
    const safeWidth = marqueeRepeatedItemWidth === 0 ? 1 : marqueeRepeatedItemWidth;

    return Math.ceil(marqueeWidth / safeWidth);
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}